import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import StatsCards from "./StatsCards";
import AccountsHeader from "./Header";
import NavigationTabs from "./NavigationTabs";
import ChartsSection from "./ChartSection";
import { useGetAccountsDashboardQuery } from "../../../redux/api/account";
import { selectCurrentagency } from "../../../redux/features/authSlice";


const MONTHS = [
  "Jan","Feb","Mar","Apr","May","Jun",
  "Jul","Aug","Sep","Oct","Nov","Dec"
];
const monthLabelToNumber = (label) => {
  if (!label) return 1;
  const shortLabel = label.slice(0, 3); // e.g., "October" → "Oct"
  const index = MONTHS.indexOf(shortLabel);
  return index >= 0 ? index + 1 : 1;
};

const now = new Date();
const CURRENT_MONTH_LABEL = MONTHS[now.getMonth()];
const CURRENT_YEAR = now.getFullYear();

const AccountsDashboard = () => {
  const [month, setMonth] = useState(CURRENT_MONTH_LABEL);
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const currentAgency = useSelector(selectCurrentagency);
  const [search, setSearch] = useState("");

  const monthNum = monthLabelToNumber(month);
  const yearNum = Number(year);

  const { data, isLoading, isFetching, isError } = useGetAccountsDashboardQuery(
    currentAgency?.agencyid
      ? { agencyId: currentAgency.agencyid, month: monthNum, year: yearNum }
      : { agencyId: 0, month: monthNum, year: yearNum },
    { skip: !currentAgency?.agencyid } 
  );


  const totalBalance = data?.totalBalance ?? 0;
  const totalAdvance = data?.totalAdvance ?? 0;
  const totalExpenses = data?.totalexpanses ?? 0;
  const totalInstallmentPaid = data?.totalinstallmentpaid ?? 0;
  const totalInstallmentNotPaid = data?.totalinstallmentnotpaid ?? 0;
  const totalSalaries = data?.totalsalaries ?? 0;
  const totalDealsAmount = data?.totaldealsamount ?? 0;

  const incomeVsExpensesSeries = useMemo(() => {
    if (!data) return [];
    return [
      {
        name: data.monthwiseincome?.name || "Income",
        data: Array.isArray(data.monthwiseincome?.data)
          ? data.monthwiseincome.data
          : [],
      },
      {
        name: data.monthlyExpensesTotals?.name || "Expenses",
        data: Array.isArray(data.monthlyExpensesTotals?.data)
          ? data.monthlyExpensesTotals.data
          : [],
      },
    ];
  }, [data]);

  const profitTrendSeries = useMemo(() => {
    if (!data) return [];

    const incomeArr = Array.isArray(data.monthwiseincome?.data)
      ? data.monthwiseincome.data
      : [];
    const expenseArr = Array.isArray(data.monthlyExpensesTotals?.data)
      ? data.monthlyExpensesTotals.data
      : [];

    const profitArr = incomeArr.map((inc) => inc);

    return [{ name: "Profit", data: profitArr }];
  }, [data]);

 
  const incomeVsExpensesOptions = useMemo(
    () => ({
      legend: { show: true },
      xaxis: { categories: MONTHS },
      colors: ["#10b981", "#f59e0b"],
    }),
    []
  );

  const profitTrendOptions = useMemo(
    () => ({
      legend: { show: false },
      xaxis: { categories: MONTHS },
      colors: ["#10b981"], 
      markers: {
        size: 4,
        colors: ["#fff"],
        strokeColors: "#10b981",
        strokeWidth: 2,
        hover: { size: 5, colors: ["#fff"], strokeColors: "#10b981" },
      },
    }),
    []
  );

 const currency =
    currentAgency?.countryid === 16
      ? "Rs"
      : currentAgency?.countryid === 18
      ? "AED"
      : "";

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: "10px",
        minHeight: "70vh",
        background: (t) =>
          t.palette.mode === "dark"
            ? "linear-gradient(to bottom, #16241c 0%, #12161f 100%)"
            : "linear-gradient(to bottom, #edfef6 0%, #ffffff 100%)",
      }}
    >
      <AccountsHeader
        month={month}
        setMonth={setMonth}
        year={year}
        setYear={setYear}
        search={search}
        setSearch={setSearch}
      />

      <StatsCards
        totalBalance={totalInstallmentPaid}
        totalAdvance={totalAdvance}
        totalExpenses={totalExpenses}
        totalInstallmentPaid={totalInstallmentPaid}
        totalInstallmentNotPaid={totalInstallmentNotPaid}
        totalSalaries={totalSalaries}
        totalDealsAmount={totalDealsAmount}
        currency={currency}
      />

      <ChartsSection
        incomeVsExpensesOptions={incomeVsExpensesOptions}
        incomeVsExpensesSeries={incomeVsExpensesSeries}
        profitTrendOptions={profitTrendOptions}
        profitTrendSeries={profitTrendSeries}
        currency={currency}
      />

      <NavigationTabs />

      {isLoading || isFetching ? (
        <div style={{ fontSize: 12, opacity: 0.6, marginTop: 8 }}>
          Loading dashboard…
        </div>
      ) : isError ? (
        <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 8 }}>
          Failed to load dashboard.
        </div>
      ) : null}
    </Box>
  );
};

export default AccountsDashboard;
